import { useId, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

import { CloseIcon } from '../internal/icons';
import { useOverlay } from '../internal/useOverlay';
import { IconButton } from './IconButton';
import styles from './Drawer.module.css';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  eyebrow?: ReactNode;
  side?: 'right' | 'left';
  /** `inspector` matches the canvas inspector rail; `wide` is for a diff view. */
  width?: 'inspector' | 'side' | 'wide';
  footer?: ReactNode;
  /**
   * Modal drawers scrim the page and trap Tab. A non-modal drawer (an inspector
   * kept open while the user works the canvas) does neither.
   */
  modal?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  closeLabel?: string;
  className?: string;
  children: ReactNode;
}

/** Edge-anchored dialog. Escape closes and focus returns to the trigger. */
export function Drawer({
  open,
  onClose,
  title,
  eyebrow,
  side = 'right',
  width = 'inspector',
  footer,
  modal = true,
  initialFocusRef,
  closeLabel = 'Close',
  className,
  children,
}: DrawerProps) {
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const ref = useOverlay({
    open,
    onClose,
    trap: modal,
    initialFocus: initialFocusRef,
    lockScroll: modal,
  });

  if (!open) return null;

  return createPortal(
    <div className={clsx(styles.root, modal ? styles.modal : styles.inline, styles[side])}>
      {modal && <div className={styles.scrim} onClick={onClose} data-testid="drawer-scrim" />}
      <div
        ref={ref}
        role="dialog"
        aria-modal={modal ? 'true' : 'false'}
        aria-labelledby={titleId}
        tabIndex={-1}
        className={clsx(styles.panel, styles[width], styles[`from-${side}`], className)}
      >
        <header className={styles.header}>
          <div className={styles.headerText}>
            {eyebrow !== undefined && <p className={styles.eyebrow}>{eyebrow}</p>}
            <h2 className={styles.title} id={titleId}>
              {title}
            </h2>
          </div>
          <IconButton label={closeLabel} icon={<CloseIcon />} onClick={onClose} />
        </header>
        <div className={styles.body}>{children}</div>
        {footer !== undefined && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
